import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { IdCard, Trash2, Upload, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@components/button/button";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { useCriarCamposVariaveisModelo } from "@/hooks/modelos/criar-campos-variaveis-modelos";
import { useCriarModelo } from "@/hooks/modelos/criar-modelo";
import { useListarProdutosAtivosSelecao } from "@/hooks/produtos/listar-produtos-ativos-selecao";
import { useBuscarTecnologias } from "@/hooks/tecnlogias/buscar-tecnologias";

import { notifyAlert } from "@/utils/notify-messages";

const schema = z.object({
  cliente: z.string().min(1, "Cliente é obrigatório"),
  produto: z.string().min(1, "Selecione um produto"),
  nomeModelo: z.string().min(1, "Nome do modelo é obrigatório"),
  fotoModelo: z.instanceof(FileList).optional(),
  temFuro: z.boolean().optional().default(false),
  posicionamento: z.enum(["Vertical", "Horizontal"]),
  tipoFuro: z.enum(["Ovoíde", "Redondo"]).optional(),
  temCargaFotos: z.boolean().optional().default(false),
  temDadosVariaveis: z.boolean().optional().default(false),
  isProvisorio: z.boolean().optional().default(false),
  campoChave: z.string().optional(),
  observacoes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

type ICreateModeloModalProps = {
  handleToggleModal: () => void;
};

type ICamposVariaveis = {
  id: number;
  nome: string;
  obrigatorio: boolean;
  ordem: number;
};
function removerAcentos(str: string) {
  return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : "";
}

const CreateModeloModal = ({ handleToggleModal }: ICreateModeloModalProps) => {
  const location = useLocation();
  const { userCliente, nome } = location.state || {};
  const { produtos } = useListarProdutosAtivosSelecao();
  const { tecnologias, isLoadingTecnologias } = useBuscarTecnologias({ somenteAtivos: true });

  const { criarModeloAsync } = useCriarModelo();
  const { criarCamposVariaveisModeloAsync } = useCriarCamposVariaveisModelo();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { posicionamento: "Vertical" },
  });

  const [frontFile, setFrontFile] = useState<string | null>(null);
  const [backFile, setBackFile] = useState<string | null>(null);

  const [imagemAmpliada, setImagemAmpliada] = useState<string | null>(null);

  const [fileFrente, setFileFrente] = useState<File | null>(null);
  const [fileVerso, setFileVerso] = useState<File | null>(null);

  const [isLoadingCriacaoModelo, setIsLoadingCriacaoModelo] = useState(false);

  const [isChecked, setIsChecked] = useState(false);
  const [isCampoVariaveis, setIsCampoVariaveis] = useState(false);

  const [camposVariaveis, setCamposVariaveis] = useState<ICamposVariaveis[]>([]);
  const [campo, setCampo] = useState("");
  const [campoObrigatorio, setCampoObrigatorio] = useState(false);
  const [messageError, setMessageError] = useState({
    error: false,
    message: "",
  });

  const [tecnologiasSelecionadas, setTecnologiasSelecionadas] = useState<number[]>([7]);

  const handleFrontFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileFront = event.target.files[0];

      setFileFrente(fileFront);
      const imageUrl = URL.createObjectURL(fileFront);
      setFrontFile(imageUrl);
    }
  };

  const handleBackFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileBack = event.target.files[0];

      setFileVerso(fileBack);
      const imageUrl = URL.createObjectURL(fileBack);
      setBackFile(imageUrl);
    }
  };

  const handleAddCamposVariaveis = useCallback(() => {
    if (!campo) {
      setMessageError({
        error: true,
        message: "Preencha o nome do campo variável",
      });
      return;
    }
    setCamposVariaveis([
      ...camposVariaveis,
      {
        id: Math.floor(Math.random() * 1000000) + 1,
        ordem: camposVariaveis.length + 1,
        nome: campo,
        obrigatorio: campoObrigatorio,
      },
    ]);
    setMessageError({
      error: false,
      message: "",
    });

    setCampo("");
  }, [campo, camposVariaveis, campoObrigatorio]);

  const handleRemoveCamposVariaveis = useCallback(
    (campo_id: number) => {
      if (!campo_id) return;

      const result = camposVariaveis.filter((item) => item.id !== campo_id);

      setCamposVariaveis(result);
    },
    [camposVariaveis]
  );

  // extrai tecnologias únicas com id + nome
  const tecnologiasUnicas = useMemo(() => {
    const mapa = new Map<number, { id: number; nome: string }>();
    tecnologias.forEach((item) => {
      if (!mapa.has(item.id)) {
        mapa.set(item.id, { id: item.id, nome: item.nome }); // ou item.nome, dependendo do campo
      }
    });
    return Array.from(mapa.values());
  }, [tecnologias]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        setIsLoadingCriacaoModelo(true);

        const produtoSelecionado = produtos.find((item) => item.nome === data.produto);

        if (!produtoSelecionado) {
          notifyAlert("Você deve selecionar um um produto!");
          return;
        }

        if (data.temDadosVariaveis && !data?.campoChave) {
          notifyAlert("Campo chave é obrigatório quando dados variáveis estiverem selecionados!");
          return;
        }

        if (!fileFrente && !fileVerso) {
          notifyAlert("Você deve selecionar um arquivo de foto!");
          return;
        }

        const dataModelo = {
          user_id: userCliente,
          produto_id: produtoSelecionado.id,
          tecnologia_id: tecnologiasSelecionadas[0],
          nome_modelo: data.nomeModelo,
          tipo_furo: removerAcentos(data?.tipoFuro?.toLocaleLowerCase()!),
          posicionamento: data.posicionamento,
          tem_furo: data.temFuro,
          tem_carga_foto: data.temCargaFotos,
          tem_dados_variaveis: data.temDadosVariaveis,
          is_provisorio: data.isProvisorio,
          campo_chave: data.campoChave,
          foto_frente: fileFrente,
          foto_verso: fileVerso,
          observacoes: data.observacoes,
        };

        const modeloCriado = await criarModeloAsync(dataModelo as any);
        if (!modeloCriado?.data?.id) return;

        const camposVariaveisFormatados = camposVariaveis.map((campo, index) => ({
          id: campo.id ?? null,
          modelo_tecnico_id: modeloCriado?.data?.id,
          nome: campo.nome,
          obrigatorio: campo.obrigatorio,
          ordem: index + 1,
        }));

        await criarCamposVariaveisModeloAsync({
          campos: camposVariaveisFormatados,
        });

        handleToggleModal();
      } catch (error: any) {
        // Tratar erro do Zod se for um erro de validação
        if (error instanceof z.ZodError) {
          const mensagens = error.errors.map((e) => `• ${e.message}`).join("\n");
          alert(`Erros de validação:\n${mensagens}`);
        } else {
          alert("Erro inesperado ao criar o modelo. Verifique os dados e tente novamente.");
        }
      } finally {
        setIsLoadingCriacaoModelo(false);
      }
    },
    [
      produtos,
      tecnologiasSelecionadas,
      userCliente,
      fileFrente,
      fileVerso,
      criarModeloAsync,
      criarCamposVariaveisModeloAsync,
      camposVariaveis,
      handleToggleModal,
    ]
  );

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setValue("produto", event.target.value as any);
  };

  useEffect(() => {
    setValue("cliente", nome);
  }, [setValue, nome]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60">
      <div className="h-[600px] w-[840px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IdCard className="text-zinc-800" size={24} />
            <h2 className="text-xl font-semibold text-zinc-800">Cadastro de Modelo</h2>
          </div>
          <button onClick={handleToggleModal}>
            <X className="size-5 text-zinc-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-4">
            {/* Cliente */}
            <div>
              <p className="mb-2 text-zinc-800">Cliente</p>
              <input
                {...register("cliente")}
                placeholder="Cliente"
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                // value={nome}
              />
              {errors.cliente && <p className="text-red-500">{errors.cliente.message}</p>}
            </div>

            {/* Produto */}
            <div>
              <p className="text-zinc-800">Produto</p>
              <select
                {...register("produto")}
                className="flex h-12 w-full items-center rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                onChange={handleSelectChange}
              >
                <option value="">Selecione o produto</option>
                {produtos.map((produto) => (
                  <option key={produto.id} value={produto.nome}>
                    {produto.nome}
                  </option>
                ))}
              </select>
              {errors.produto && <p className="text-red-500">{errors.produto.message}</p>}
            </div>

            {/* Nome do Modelo */}
            <div>
              <p className="mb-2 text-zinc-800">Nome do modelo</p>
              <input
                {...register("nomeModelo")}
                placeholder="Nome do Modelo"
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              />
              {errors.nomeModelo && <p className="text-red-500">{errors.nomeModelo.message}</p>}
            </div>

            {/* Envio de fotos */}
            <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-gray-300 bg-white p-4 shadow-md">
              <p className="text-lg font-semibold text-zinc-800">Envio de fotos do modelo</p>

              <div className="flex w-full gap-4">
                {/* Upload da Frente */}
                <div className="flex w-1/2 flex-col items-center">
                  <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-all">
                    <Upload size={20} />
                    <span className="text-sm">Foto Frente</span>
                    <input type="file" onChange={handleFrontFileChange} className="hidden" />
                  </label>

                  {frontFile && (
                    <img
                      src={frontFile}
                      alt="Foto Frente"
                      className="mt-4 max-h-40 w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-100 object-contain shadow-md"
                      onClick={() => setImagemAmpliada(frontFile)}
                    />
                  )}
                </div>

                {/* Upload do Verso */}
                <div className="flex w-1/2 flex-col items-center">
                  <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-all">
                    <Upload size={20} />
                    <span className="text-sm">Foto Verso</span>
                    <input type="file" onChange={handleBackFileChange} className="hidden" />
                  </label>
                  {backFile && (
                    <img
                      src={backFile}
                      alt="Foto Verso"
                      className="mt-4 max-h-40 w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-100 object-contain shadow-md"
                      onClick={() => setImagemAmpliada(backFile)} //
                    />
                  )}
                </div>
              </div>

              {imagemAmpliada && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
                  <div className="relative">
                    <button
                      className="absolute right-4 top-4 rounded-full bg-white p-2 shadow"
                      onClick={() => setImagemAmpliada(null)}
                    >
                      <X className="text-black" />
                    </button>
                    <img
                      src={imagemAmpliada}
                      alt="Visualização ampliada"
                      className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {/**TECNOLOGIA */}
            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-gray-300 bg-white p-4 shadow-md">
              <p className="text-zinc-800">Tipo de tecnologia</p>
              {isLoadingTecnologias ? (
                <div className="flex h-32 w-full items-center justify-center">
                  <SpinnerSimple color="zinc-600" />
                </div>
              ) : (
                <div className="flex w-full flex-row flex-wrap items-center gap-8">
                  {tecnologiasUnicas.map((tec) => (
                    <label key={tec.id} className="flex items-center gap-2 text-zinc-800">
                      <input
                        type="radio"
                        name="tecnologia"
                        checked={tecnologiasSelecionadas[0] === tec.id}
                        onChange={() => setTecnologiasSelecionadas([tec.id])}
                      />
                      {tec.nome}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Checkboxes */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  type="checkbox"
                  {...register("temFuro")}
                  onChange={(event) => {
                    setValue("temFuro", event.target.checked);
                    setIsChecked(event.target.checked);
                  }}
                />
                Tem furo
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    setValue("temCargaFotos", e.target.checked);
                  }}
                />
                Possui Fotos
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    setValue("temDadosVariaveis", e.target.checked);
                    setIsCampoVariaveis(e.target.checked);
                  }}
                />
                Possui dados variáveis
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  type="checkbox"
                  {...register("isProvisorio")}
                  onChange={(e) => {
                    setValue("isProvisorio", e.target.checked);
                  }}
                />
                Modelo provisório
              </label>
            </div>

            {/* TIPO DE FURO */}
            {isChecked && (
              <div>
                <p className="mb-2 text-zinc-800">Selecione o tipo de furo</p>
                <select
                  {...register("tipoFuro")}
                  className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                >
                  <option value="Ovoíde">Ovoíde</option>
                  <option value="Redondo">Redondo</option>
                </select>
              </div>
            )}

            {/* Posicionamento */}
            <div>
              <p className="mb-2 text-zinc-800">Posicionamento</p>
              <select
                {...register("posicionamento")}
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              >
                <option value="Vertical">Vertical</option>
                <option value="Horizontal">Horizontal</option>
              </select>
            </div>

            {/* Campo Chave */}
            {getValues("temDadosVariaveis") && (
              <div>
                <p className="mb-2 text-zinc-800">Campo chave</p>
                <input
                  {...register("campoChave")}
                  placeholder="Informe o campo chave"
                  className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                />
                {errors.campoChave && <p className="text-red-500">{errors.campoChave.message}</p>}
              </div>
            )}

            {/**CAMPOS VARIÁVEIS */}
            {isCampoVariaveis && (
              <div>
                <p className="mb-2 text-zinc-800">Campos variáveis</p>

                <div className="flex items-center">
                  <input
                    placeholder="Informe o nome do campo variável"
                    className="flex h-14 w-10/12 items-center gap-2 rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                    onChange={(e) => setCampo(e.target.value)}
                    value={campo}
                  />

                  <Button
                    size="sm"
                    variant="default"
                    className="ml-2 h-14 gap-1 text-neutral-50"
                    type="button"
                    onClick={handleAddCamposVariaveis}
                  >
                    <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                      Adicionar Campo
                    </span>
                  </Button>
                </div>

                {messageError.error && <p className="text-red-500">{messageError.message}</p>}

                <label className="mt-2 flex items-center gap-2 text-zinc-800">
                  <input type="checkbox" onChange={(e) => setCampoObrigatorio(e.target.checked)} />
                  Obrigátorio?
                </label>

                {camposVariaveis?.length > 0 && (
                  <table className="mt-4 w-full border-collapse overflow-hidden rounded-lg">
                    <thead className="bg-bg-table-row">
                      <tr className="text-left text-gray-700">
                        <th className="border-b px-4 py-2">#</th>
                        <th className="border-b px-4 py-2">Nome</th>
                        <th className="border-b px-4 py-2">Obrigatório</th>
                        <th className="border-b px-4 py-2 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {camposVariaveis?.map((item, index) => (
                        <tr
                          key={item.id}
                          className="text-zinc-800 transition-all hover:bg-gray-100"
                        >
                          <td className="border-b px-4 py-3">{index + 1}</td>
                          <td className="border-b px-4 py-3">{item.nome}</td>
                          <td className="border-b px-4 py-3">{item.obrigatorio ? "Sim" : "Não"}</td>
                          <td className="border-b px-4 py-3 text-center">
                            <button
                              type="button"
                              className="rounded-md bg-red-500 p-2 text-white transition-all hover:bg-red-600"
                              onClick={() => handleRemoveCamposVariaveis(item.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Observações */}
            <div>
              <p className="mb-2 text-zinc-800">Observações</p>
              <textarea
                {...register("observacoes")}
                placeholder="Digite informações adicionais"
                className="h-20 w-full rounded-lg border border-zinc-800 px-4 pt-4 text-zinc-800 outline-none"
              />
            </div>
          </div>

          <div className="flex w-full justify-end">
            <Button disabled={isLoadingCriacaoModelo} type="submit" className="w-full">
              {isLoadingCriacaoModelo ? <SpinnerSimple /> : "  Salvar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { CreateModeloModal };
